import { Button, Flex, FormLabel, Stack } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import { SingleDatepicker } from 'components/DatePicker';
import Link from 'components/Link';
import { useAxios } from 'context/AxiosContextProvider';
import { useUserContext } from 'context/UserContextProvider';
import { endOfDay, format, startOfDay, subHours } from 'date-fns';
import { isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IdentificationResultIcon } from '../../components/Icons';
import AlertModal from '../../components/modals/AlertModal';
import { DataTable } from '../../components/Table/DataTable';
import { SelectColumnFilter } from '../../components/Table/Filter';
import localconfig from '../../config';
import { createDropdownTranslatedStaticOptions } from '../../helpers/general';
import useAnnouncementsQuery from '../../hooks/useAnnouncementsQuery';
import { ContentCard } from '../../layout/ContentCard';

const initialFilters = {
  searchString: '',
  itemsPerPage: 20,
  country: undefined,
  startDate: null,
  endDate: null,
};

const AnnouncementOverviewCard = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { config } = useUserContext();
  const axios = useAxios();

  const localStorageData = window.localStorage.getItem('announcement_filters');
  const destringifiedData = JSON.parse(localStorageData!) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('announcement_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [country, setCountry] = useState(destringifiedData?.country || initialFilters?.country);

  const [startDate, setStartDate] = useState(destringifiedData.startDate || initialFilters?.startDate);
  const [endDate, setEndDate] = useState(destringifiedData.endDate || initialFilters?.endDate);

  const [filtersSelected, setFiltersSelected] = useState(false);

  const formatStartDateNoHours = (date: string) => {
    return format(startOfDay(new Date(date)), 'dd-MM-yyyy');
  };
  const formatEndDateNoHours = (date: string) => {
    return format(
      // @ts-ignore
      subHours(new Date(date), 11),
      'dd-MM-yyyy',
    );
  };

  const formatStart = (date: string | null) => {
    if (date === null) return null;
    return format(startOfDay(new Date(date)), 'yyyy-MM-dd HH:mm:ss');
  };

  const formatEnd = (date: string | null) => {
    if (date === null) return null;
    return format(endOfDay(new Date(date)), 'yyyy-MM-dd HH:mm:ss');
  };

  const formattedStart = formatStart(startDate);
  const formattedEnd = formatEnd(endDate);

  const announcementsQuery = useAnnouncementsQuery({
    searchString,
    page,
    itemsPerPage,
    country,
    startDate: formattedStart,
    endDate: formattedEnd,
  });

  const initialLoad = searchString === '' && itemsPerPage === 20 && page === 1 && isNil(country);
  const sendRequest = useCallback(
    async ({ url, method = 'put', dataForEndpoint = {} }: { url: string; method: string; dataForEndpoint?: Record<string, any> }) => {
      const axiosCall =
        method !== 'delete'
          ? // @ts-ignore
            axios[method](url, dataForEndpoint)
          : // @ts-ignore
            axios[method](url);
      return await axiosCall
        .catch((err: { response: { status: number } }) => {
          return Promise.reject(err);
        }) // @ts-ignore
        .then(() => {
          return queryClient.invalidateQueries({
            queryKey: useAnnouncementsQuery.getKey(searchString, page, itemsPerPage, country, startDate, endDate),
          });
        });
    },
    [axios, country, endDate, itemsPerPage, page, queryClient, searchString, startDate],
  );

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage, startDate, endDate]);

  useEffect(() => {
    setFiltersSelected(!isNil(country) || !isNil(endDate) || !isNil(startDate));

    window.localStorage.setItem(
      'announcement_filters',
      JSON.stringify({
        searchString: searchString,
        country: country,
        itemsPerPage: itemsPerPage,
        startDate: startDate,
        endDate: endDate,
      }),
    );
  }, [searchString, country, itemsPerPage, startDate, endDate]);

  const columns = useMemo(
    () => [
      {
        Header: t('forms:fullname.label'),
        accessor: 'name',
      },
      {
        Header: t('forms:start.label'),
        accessor: (row: { start: string }) => formatStartDateNoHours(row.start) ?? '-',
        shouldFilter: true,
        Filter: () => (
          <>
            <FormLabel mt={5} size="lg">
              {t('forms:start_date.label')}
            </FormLabel>
            <SingleDatepicker
              //@ts-ignore
              date={startDate ? formattedStart : undefined}
              name={'dateStart'}
              onDateChange={(date: Date) => setStartDate(date)}
              maxDate={endDate ? new Date(endDate) : undefined}
              configs={{ dateFormat: 'dd.MM.yyyy' }}
            />
          </>
        ),
      },
      {
        Header: t('forms:end.label'),
        accessor: (row: { end: string }) => formatEndDateNoHours(row.end) ?? '-',
        shouldFilter: true,
        Filter: () => (
          <>
            <FormLabel mt={5} size="lg">
              {t('forms:end_date.label')}
            </FormLabel>
            <SingleDatepicker
              //@ts-ignore
              date={endDate ? formattedEnd : undefined}
              name={'dateEnd'}
              onDateChange={(date: Date) => setEndDate(date)}
              minDate={startDate ? new Date(startDate) : undefined}
              configs={{ dateFormat: 'dd.MM.yyyy' }}
            />
          </>
        ),
      },
      {
        Header: t('forms:countries.label'),
        accessor: (row: any) => row.country.join(', '),
        shouldFilter: true,
        //@ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: country,
            column,
            dropdownOptions: createDropdownTranslatedStaticOptions({
              records: config?.countries,
              translationKey: 'location:countries',
            }),
            onChange: setCountry,
          }),
      },
      {
        id: 'button',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;

          return (
            <Flex alignItems={'flex-end'}>
              <Button ml={'auto'}>
                <Link to={`/app/op/announcements/${row.original.id}/update`} data-test-id={'edit-' + row.index}>
                  {t('common:edit')}
                </Link>
              </Button>
            </Flex>
          );
        },
      },
      {
        id: 'delete',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          if (!row.original.workshop_user_id) {
            return (
              <Flex alignItems={'flex-end'}>
                <Button
                  ml={'auto'}
                  color="red.500"
                  onClick={() => {
                    NiceModal.show(AlertModal, {
                      onSubmit: () =>
                        sendRequest({
                          method: 'delete',
                          url: `${localconfig.apiBaseUrl}/op/v1/announcement/${row.original.id}`,
                        }).then(() => {
                          return queryClient.invalidateQueries({
                            queryKey: useAnnouncementsQuery.getKey(
                              searchString,
                              page,
                              itemsPerPage,
                              country,
                              formattedStart ?? undefined,
                              formattedEnd ?? undefined,
                            ),
                          });
                        }),
                      children: t('pages:announcements.modal.delete.description'),
                      content: {
                        header: t('pages:announcements.modal.delete.header'),
                        footer: {
                          buttons: {
                            cancelCaption: t('common:cancel'),
                            actionCaption: t('common:delete'),
                          },
                        },
                      },
                    });
                  }}
                >
                  {t('common:delete')}
                </Button>
              </Flex>
            );
          }
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchString, country, startDate, endDate, announcementsQuery.data],
  );

  return (
    <ContentCard
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('pages:announcements.table.header')}
      data-test-id="announcements-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setStartDate(null);
              setEndDate(null);
              setPage(1);
              setCountry(initialFilters?.country);
              setItemsPerPage(initialFilters?.itemsPerPage);
              window.localStorage.setItem('announcement_filters', JSON.stringify(initialFilters));
            }}
            data-test-id="announcements-table"
            data={(announcementsQuery.data?.data as any[]) || []}
            globalFilter={searchString}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={true}
            globalFilterPlaceholder={t('promotions:table.search')}
            // @ts-ignore
            hasFilters={true}
            hasSearchInput={true}
            currentPage={announcementsQuery?.data?.current_page ? announcementsQuery?.data?.current_page - 1 : 0}
            perPage={itemsPerPage}
            filtersSelected={filtersSelected}
            total={announcementsQuery.data?.last_page}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={announcementsQuery.isLoading || announcementsQuery.isFetching}
            isMoreThenOnePage={!initialLoad || Boolean(announcementsQuery.data?.next_page_url)}
          />
        </Stack>
      </Stack>
      <Flex direction="row-reverse" marginTop={2}>
        <Button variant="primary" as={Link} to="/app/op/announcements/create">
          {t('common:create')}
        </Button>
      </Flex>
    </ContentCard>
  );
};

export default AnnouncementOverviewCard;
